import PropTypes from 'prop-types';
import { formatDistanceToNowStrict } from 'date-fns';
// @mui
import { styled } from '@mui/material/styles';
import { Avatar, Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import DownloadFileContainer from '../../../components/DownloadFileContainer';
import Image from '../../../fl-components/Image';
import { checkIsImage } from '../../../helper/commonHelper';
import MenuPopover from '../../../components/MenuPopover';
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(3)
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 320,
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,

  '&:hover': {
    cursor: 'pointer'
  }
}));

const InfoStyle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(0.75),
  color: theme.palette.text.secondary
}));

// ----------------------------------------------------------------------

ChatMessageItem.propTypes = {
  message: PropTypes.object.isRequired,
  onOpenLightbox: PropTypes.func,
  deleteMessage: PropTypes.func,
  handleHeightChange: PropTypes.func
};

export default function ChatMessageItem({
  message,
  onOpenLightbox,
  deleteMessage,
  handleHeightChange
}) {
  const [openModal, setModal] = useState(false);
  const messageRef = useRef();
  // const sender = conversation.participants.find(
  //   (participant) => participant.id === message.senderId
  // );
  const currentUser = useSelector((state) => state.auth.currentUser);
  const senderDetails =
    message.user._id === currentUser.id
      ? { type: 'me' }
      : { avatar: message?.user?.avatar, name: message?.user.name };

  const isMe = senderDetails.type === 'me';
  const isImage = message?.file && !message.isDeleted && checkIsImage(message.fileType);
  const isHavingFile = message?.file && !message.isDeleted && !checkIsImage(message.fileType);
  const firstName = senderDetails.name && senderDetails.name.split(' ')[0];

  return (
    <RootStyle>
      <Box
        sx={{
          display: 'flex',
          ...(isMe && {
            ml: 'auto'
          })
        }}
      >
        {senderDetails.type !== 'me' && (
          <Avatar
            alt={senderDetails.name}
            src={senderDetails.avatar}
            sx={{ width: 32, height: 32, mr: 2 }}
          />
        )}

        <div>
          <InfoStyle
            variant="caption"
            sx={{
              ...(isMe && { justifyContent: 'flex-end' })
            }}
          >
            {!isMe && `${firstName},`}&nbsp;
            {formatDistanceToNowStrict(new Date(message.createdAt), {
              addSuffix: true
            })}
          </InfoStyle>

          <ContentStyle
            sx={{
              ...(isMe && { color: 'grey.800', bgcolor: 'primary.lighter' }),
              ...(isImage && { p: 0 }),
              ...(isHavingFile && { p: 0 })
            }}
            onClick={
              message.user._id === currentUser.id && !message.isDeleted
                ? () => {
                    setModal(true);
                  }
                : () => {}
            }
            ref={messageRef}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {message.isDeleted
              ? message.user._id === currentUser.id
                ? 'You deleted this message'
                : 'This message is deleted'
              : null}
            {isHavingFile && (
              <DownloadFileContainer
                title={message.fileName}
                size={{
                  width: 30,
                  height: 30
                }}
                isDownload
              />
            )}
            {isImage && (
              <Image
                alt="attachment"
                src={message.file}
                // onClick={() => openImageViewer([message.file])}
                onClick={() => onOpenLightbox(message.file)}
                sx={{ borderRadius: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                afterLoad={() => handleHeightChange()}
              />
            )}
            {message.text && !message.isDeleted && (
              <Typography variant="body2">{message.text}</Typography>
            )}
          </ContentStyle>
        </div>
      </Box>
      <MenuPopover
        open={openModal}
        onClose={() => {
          setModal(false);
        }}
        anchorEl={messageRef.current}
        sx={{ width: 220 }}
      >
        <List>
          <ListItem
            button
            onClick={() => {
              setModal(false);
              deleteMessage(message.firebaseId);
            }}
          >
            <ListItemText primary="Delete" />
          </ListItem>
        </List>
      </MenuPopover>
    </RootStyle>
  );
}
